import { EventEmitter, Injectable } from "@angular/core";
import { SortDirection } from "@angular/material/sort";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiEndpointsService } from "src/app/core/services/api-endpoints.service";
import { ApiHttpService } from "src/app/core/services/api-http.service";
import { convertWithDecimal } from "src/app/utils/numberConvertion.util";
import { CategoryResponse } from "./invoice.interface";

export interface IRouteParams {
    vehicleId: string;
    entityId: string;
}
interface ILinkedTransaction {
    invoiceId: string;
    transactionIds: string[];
}
interface IPayload {
    transactionType: string[];
  }
@Injectable({
    providedIn: 'root'
})
export class InvoiceDetailService {

    public openInvoiceDetail: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public invoiceDetails: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    
    public selectedVehicle: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    public openQrCode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public entityUserToken: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    entityUserTokenAsObservables$ = this.entityUserToken.asObservable();

    qrCodeAsObservables$ = this.openQrCode.asObservable();

    invoiceDetailsAsObservables$ = this.invoiceDetails.asObservable();

    selectedVehicleAsObservables$ = this.selectedVehicle.asObservable();

    openInvoiceDetailAsObservables$ = this.openInvoiceDetail.asObservable();

    public routeParams: BehaviorSubject<IRouteParams | null> = new BehaviorSubject<IRouteParams | null>(null);

    routeParamsAsObservables$ = this.routeParams.asObservable();


    valueDeleted = new EventEmitter<boolean>();


    constructor(
        private apiEndpointsService: ApiEndpointsService,
        private apiHttpService: ApiHttpService
    ) { }


    public openInvoiceDetailDrawer(): void {
        this.openInvoiceDetail.next(true);
    }

    public closeInvoiceDetailDrawer(): void {
        this.openInvoiceDetail.value && this.openInvoiceDetail.next(false);
    }


    public emitValueDeleted(): void {
        this.valueDeleted.emit(true);
    }


    getAllBankAccountsForCounterPart(vehicleId: string, counterPartId: string): Observable<any>  {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `counterparties/${counterPartId}/bank-accounts?limit=100`,
                false
            )
        );
    }


    getAllCounterParts(vehicleId:string){
        return this.apiHttpService.get(this.apiEndpointsService.createUrlV2(
            `vehicles/${vehicleId}/counterparties?limit=100`,
            false
        ));
    }

    getCounterPart(vehicleId: string, counterpartId: string) {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrl(
                `counterparts/${vehicleId}/${counterpartId}`,
                false
            )
        );
    }

    getInvoiceDetails(vehicleId: string, invoiceId: string):Observable<any>  {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/details`,
                false
            )
        );
    }
    getPayableLineItems(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrl(
                `vehicles/${vehicleId}/payables/${invoiceId}/line-items`,
                false
            )
        );
    }
    updateInvoiceDetails(vehicleId: string, invoiceId: string, data: any) {
        return this.apiHttpService.put(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/update`,
                false
            ),
            {data}
        );
    }

    updateBankAccountDetails(
        vehicleId: string,
        counterpartyId: string,
        bankAccountId: string,
        data: any
    ) {
        return this.apiHttpService.put(
            this.apiEndpointsService.createUrlV2(
                `counterparties/${counterpartyId}/bank-accounts/${bankAccountId}`,
                false
            ),
            {data}
        );
    }

    viewPaymentCode(vehicleId: string, paymentId: string) {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/payment/${paymentId}/qrcode`,
                false
            )
        );
    }
    generatePaymentCode(vehicleId: string, invoiceId: string, amount: number) {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/generatepayment/${amount}`,
                false
            ),
            {}
        );
    }

    updatePayableStatusToPaid(vehicleId: string, invoiceId: string, embeddedMetaData: any) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/mark-as-paid/${embeddedMetaData.amount}`,
                false
            ),
            {reference: embeddedMetaData.reference}
        );
    }

    updatePayableStatusToApproveInProgress(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/submit-for-approval`,
                false
            ),
            {}
        );
    }

    updatePayableStatusToReopened(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrl(
                `vehicles/${vehicleId}/payables/${invoiceId}/reopen`,
                false
            ),
            {}
        );
    }


    updatePyableStatusToCancelled(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrl(
                `vehicles/${vehicleId}/payables/${invoiceId}/cancel`,
                false
            ),
            {}
        );
    }

    updatePayableStatusToRejected(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/reject`,
                false
            ),
            {}
        );
    }

    updatePayableStatusToApproved(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/approve`,
                false
            ),
            {}
        );
    }

    deleteInvoice(vehicleId: string, invoiceId: string) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `vehicle/${vehicleId}/invoice/${invoiceId}/mark-deleted`,
                false
            ),
            {}
        );
    }
    saveCommentsData(invoiceId: string, data: any) {
        return this.apiHttpService.post(
            this.apiEndpointsService.createUrlV2(
                `invoice/${invoiceId}/comments`,
                false
            ),
            {data}
        ); 
    }

    getCommentsData(invoiceId: string, page: number, pageSize: number) {
        const params: any = {};
        params.page = page;
        params.pageSize = pageSize;
        params.filter = JSON.stringify({ invoiceId: invoiceId });
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `invoice/${invoiceId}/comments`,
                false
            ),
            {params}
        ); 
    }

    getInvoiceCategoryList(): Observable<any>  {
        return this.apiHttpService.get(
            this.apiEndpointsService.createUrlV2(
                `invoice-categorization-lists?limit=100`,
                false
            ),
            {}
        );
    }
    // /bank-accounts-v2
    getTransactions(vehicleId: string, page: number, pageSize: number, params?: { [key: string]: any }, payload?: IPayload) {
        const queryParams: { [key: string | number]: string | number } = {
            ...params,
            page: page,
            pageSize: pageSize,
        };
        // Extract multi-select filters to body
        const body = {
          transactionType: payload?.transactionType || [],
        };
        const url = this.apiEndpointsService.createUrl(
          `vehicles/${vehicleId}/bank-accounts-v2/transactions`,
          false
        );
        
        return this.apiHttpService.post(url, body, { params: queryParams });
    }
    linkInvoiceToTransaction(invoiceId: string, data: any) {
        return this.apiHttpService.put(
            this.apiEndpointsService.createUrlV2(
                `transactions/link-invoice?invoiceId=${invoiceId}`,
                false
            ),
            data
        );
    }
    unlinkInvoiceToTransaction(invoiceId: string, data: any) {
        return this.apiHttpService.put(
            this.apiEndpointsService.createUrlV2(
                `transactions/unlink-invoice?invoiceId=${invoiceId}`,
                false
            ),
            data
        );
    }
}